import { Box, Button } from '@mui/material';
import { DocumentType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { ReactElement } from 'react';
import { getUrlSegment } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useNavigate } from 'react-router-dom';

import { ConvertOutcome } from '../models/convert-outcome';

const c = tokens.color;

const LABEL_BY_TYPE: Partial<Record<DocumentType, string>> = {
    [DocumentType.PurchaseOrder]: 'app.document.purchaseOrder',
    [DocumentType.Inquiry]: 'app.document.inquiry',
    [DocumentType.Invoice]: 'app.document.invoice',
    [DocumentType.Credit]: 'app.document.credit',
};

export interface ConvertResultProps {
    outcome: ConvertOutcome | null;
    onClose: () => void;
}

/**
 * What the conversion produced (#2166).
 *
 * Converting into several documents fires one call per target. Before this, the
 * screen jumped to a list when the last one came back and said nothing about
 * what had been made — and if the third call failed, the first two existed
 * anyway with nobody the wiser. The reader now sees what was created, reaches
 * it in one click, and is told when something did not work.
 */
export const ConvertResult = ({ outcome, onClose }: ConvertResultProps): ReactElement | null => {
    const navigate = useNavigate();

    if (!outcome) {
        return null;
    }

    const open = (type: DocumentType, id: number) => {
        onClose();
        navigate(`/${getUrlSegment(type)}/${id}`);
    };

    return (
        <PaloModal
            handleClose={onClose}
            isVisible={true}
            title={<FormattedMessage id="app.convertReceipt.result.title" />}
            actionChildren={[
                <Button key="close" variant="contained" color="primary" onClick={onClose}>
                    <FormattedMessage id="app.common.close" />
                </Button>,
            ]}
            actionChildrenCentered={false}
            width="sm"
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '4px 0' }}>
                {outcome.created.map(document => (
                    <Box
                        key={`${document.type}:${document.id}`}
                        role="button"
                        onClick={() => open(document.type, document.id)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '7px 10px',
                            border: `1px solid ${c.line}`,
                            borderRadius: tokens.radius.small + 'px',
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: c.surfaceAlt },
                        }}
                    >
                        <Box sx={{ fontSize: '12.5px', color: c.ink2 }}>
                            {LABEL_BY_TYPE[document.type] && <FormattedMessage id={LABEL_BY_TYPE[document.type]} />}
                        </Box>
                        <Box
                            sx={{
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '12.5px',
                                color: c.accent,
                                fontWeight: 550,
                            }}
                        >
                            {document.number ?? <FormattedMessage id="app.convertReceipt.draft" />}
                        </Box>
                    </Box>
                ))}

                {/* Naming the failure is the point: the documents above exist
                    whether or not the rest of the run worked. */}
                {outcome.failed && (
                    <Box sx={{ fontSize: '12.5px', color: c.crit, padding: '4px 2px' }}>
                        <FormattedMessage
                            id={
                                outcome.created.length > 0
                                    ? 'app.convertReceipt.result.partly'
                                    : 'app.convertReceipt.result.failed'
                            }
                        />
                    </Box>
                )}

                {!outcome.failed && 0 === outcome.created.length && (
                    <Box sx={{ fontSize: '12.5px', color: c.ink3, padding: '4px 2px' }}>
                        <FormattedMessage id="app.convertReceipt.result.none" />
                    </Box>
                )}
            </Box>
        </PaloModal>
    );
};
